import { Link, Navigate, useParams } from "react-router-dom";
import { ARTICLES } from "../data/articles";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CTABanner } from "../components/sections/CTABanner";
import { SITE_URL } from "../data/site";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/insights" replace />;

  const path = `/insights/${article.slug}`;
  const postingLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.published,
    author: { "@type": "Organization", name: "Sempresera", url: `${SITE_URL}/` },
    publisher: { "@type": "Organization", name: "Sempresera", url: `${SITE_URL}/`, logo: `${SITE_URL}/logo.png` },
    mainEntityOfPage: `${SITE_URL}${path}`,
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: article.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Seo title={article.metaTitle} description={article.metaDescription} path={path} jsonLd={[postingLd, faqLd]} />

      <article className="mx-auto max-w-3xl px-5 pb-24 pt-36 md:px-8 md:pt-44" data-testid="article-page">
        <MaskedLine delay={0.1}>
          <span className="eyebrow">
            <Link to="/insights" className="transition-colors duration-200 hover:text-white" data-testid="article-back-link">
              Insights
            </Link>
            {" · "}{article.tag}{" · "}{article.readTime}
          </span>
        </MaskedLine>
        <h1 className="mt-6 font-display text-3xl font-black leading-[1.08] tracking-tighter md:text-5xl">
          <MaskedLine delay={0.25}>
            <span className="text-metallic">{article.title}</span>
          </MaskedLine>
        </h1>

        <div className="mt-16 space-y-14">
          {article.body.map((section, i) => (
            <Reveal key={i} delay={Math.min(i * 0.05, 0.2)}>
              <section data-testid={`article-section-${i}`}>
                {section.h && (
                  <h2 className="font-display text-2xl font-bold tracking-tight text-white md:text-3xl">{section.h}</h2>
                )}
                {section.p &&
                  section.p.map((para, j) => (
                    <p key={j} className="mt-5 text-base leading-relaxed text-[#C9D2DE]">
                      {para}
                    </p>
                  ))}
                {section.stat && (
                  <div className="glass mt-8 p-8" data-testid={`article-stat-${i}`}>
                    <p className="font-display text-5xl font-black tracking-tight text-metallic">{section.stat.value}</p>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-300">{section.stat.label}</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-600">Source: {section.stat.source}</p>
                  </div>
                )}
                {section.list && (
                  <ul className="mt-6 space-y-3">
                    {section.list.map((item) => (
                      <li key={item} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 border-t border-white/10 pt-14" data-testid="article-faq">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Asked and answered.</h2>
          </Reveal>
          <div className="mt-8 space-y-5">
            {article.faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.08}>
                <details className="group glass p-6 md:p-8" data-testid={`article-faq-${i}`}>
                  <summary className="cursor-pointer list-none font-display text-lg font-bold text-white transition-colors duration-200 group-open:text-brand">
                    {f.q}
                  </summary>
                  <p className="mt-4 text-sm leading-relaxed text-[#C9D2DE]">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <p className="mt-14 text-sm text-[#A8B4C2]">
            Sources cited in this article: IBM Cost of a Data Breach Report 2025 · Verizon Data Breach
            Investigations Report (2024–2025) · Sophos State of Ransomware 2024 · FBI Internet Crime Report 2024.
          </p>
        </Reveal>
      </article>

      <CTABanner
        title="Want this handled for you?"
        sub="Book a free consultation. We'll review your setup against the risks in this article and quote the Sempresera Standard for your team."
      />
    </>
  );
}
